/**
 * Customer-template ingestion resource — typed wrappers over the
 * `/v1/twin/{data-sources,import-profiles,import,import-runs}` surface.
 *
 * A tenant uploads their own Excel/CSV export, maps its columns to their plant
 * entities, and the rows land as readings / tag-limits / batches. The client
 * parses the workbook in the browser (see @/lib/imports/parse), builds an
 * {@link ImportMapping}, and POSTs the raw rows to `/import/preview` (dry-run
 * resolution → {@link CanonicalRecord}s + counts + warnings) then `/import/apply`
 * (persist → an {@link ImportRun}). Reusable mappings save as an
 * {@link ImportProfile}; each upload is attributed to a {@link DataSource}.
 *
 * Mirrors twin-capability.ts / twin-entities.ts: a plain resource object over
 * `api.get`/`api.post`/`api.patch`/`api.delete`. Nested by resource family
 * (dataSources / profiles / runs) like the alarms module.
 */

import { api } from "@/lib/api-client";

/** One parsed spreadsheet row — cells keyed by their column header. */
export type Row = Record<string, string | number | null>;

/** A registered origin for imported data (an uploaded file, a lab feed, …). */
export interface DataSource {
  id: string;
  kind: "file" | "lab" | "gateway" | "manual";
  name: string;
  thing_id?: string;
  config: unknown;
  created_at: string;
  created_by?: string;
  /**
   * What the source's files are (one series per port, S1b): "history", the machine's own recorded log, or "report", a
   * sheet made for people; null for none, read as a report. Decides which source a port reads where two cover one time.
   * Absent from an older server.
   */
  source_role?: SourceRole | null;
  /** A person's rank (1 is read first), or null for the default its role gives. Absent from an older server. */
  rank?: number | null;
}

/** What a file source is, as a data source and an approval's stream name it. */
export type SourceRole = "history" | "report";

/**
 * A file column bound to a parameter on one of the tenant's entities.
 *
 * `record` picks the destination, and the two are not interchangeable:
 *   measurement → a reading on an entity-bound tag, timestamped from the row
 *                 (the "wide" layout: one column per parameter).
 *   condition   → a process value the lot on this row ran under, recorded on the
 *                 lot's trace rather than as a reading of its own. Only meaningful
 *                 on a `production` mapping.
 *
 * See docs/decisions/2026-07-15_imported-history-model.md (D2).
 */
export interface ImportColumn {
  header: string;
  record: "measurement" | "condition";
  entity_id: string;
  parameter: string;
  unit?: string;
  data_kind?: string;
}

/**
 * Named key columns the server reads per mode. `time`/`time_of_day` timestamp
 * measurements; `parameter`/`lower`/`upper` drive tag-limits; the production
 * keys (shift/line/part/good/scrap/defect/grade) drive batch rows.
 */
export interface ImportKeys {
  time?: string;
  time_of_day?: string;
  shift?: string;
  line?: string;
  part?: string;
  parameter?: string;
  lower?: string;
  upper?: string;
  good?: string;
  scrap?: string;
  defect?: string;
  grade?: string;
}

/** The full column→canonical mapping the server resolves rows against. */
export interface ImportMapping {
  mode: "measurements" | "limits" | "production";
  sheet?: string;
  header_row?: number;
  signature?: string[];
  keys: ImportKeys;
  columns: ImportColumn[];
  options: { entity_id?: string; date_layout?: string };
}

/** One resolved record the server would write for a source row (preview shape). */
export interface CanonicalRecord {
  kind: "measurement" | "limit" | "production" | "condition";
  row_index: number;
  entity_id: string;
  parameter?: string;
  unit?: string;
  value?: number;
  has_value?: boolean;
  ts?: string;
  has_ts?: boolean;
  lsl?: number;
  usl?: number;
  heat_number?: string;
  shift?: string;
  part?: string;
  good?: number;
  scrap?: number;
  defect?: string;
}

/**
 * A saved, reusable mapping. The backend contract fixes the create/update
 * bodies (`{ data_source_id?, name, mapping }`); the read shape is the create
 * body plus the standard envelope identity/timestamp fields.
 */
export interface ImportProfile {
  id: string;
  data_source_id?: string;
  name: string;
  mapping: ImportMapping;
  created_at: string;
  updated_at?: string;
}

/** The record of one apply — how many rows landed and as what. */
export interface ImportRun {
  id: string;
  profile_id?: string;
  data_source_id?: string;
  filename: string;
  row_count: number;
  records_written: number;
  measurements: number;
  limits_written: number;
  batches_written: number;
  /**
   * B2 (migration 0153) — what a rejects, lots or heats run wrote, which used to read
   * 0 / 0 / 0. Absent on runs recorded before these counts existed.
   */
  rejects_written?: number;
  lots_written?: number;
  edges_written?: number;
  ladle_stats_written?: number;
  /**
   * When the run keyed its rows. Absent on runs recorded before per-run undo: those
   * rows carry no stamp, so such a run cannot be undone.
   */
  ingested_at?: string;
  /** The span of the readings the run wrote; absent when it wrote none (a lots or limits sheet). */
  first_ts?: string;
  last_ts?: string;
  /**
   * B5: the first and last plant day (YYYY-MM-DD) of the data the run wrote, as the import ledger records the days it
   * still owns: the data's period, not the import's date. Only a reader's run has them, and only from the runs list.
   */
  data_from?: string;
  data_to?: string;
  status: "ok" | "partial" | "failed" | "undone";
  error?: string;
  warnings: unknown;
  created_at: string;
  undone_at?: string;
  undone_by?: string;
}

/** What an undo removed, per table, and anything it could not restore. */
export interface ImportUndoResult {
  run: ImportRun;
  removed: Record<string, number>;
  warnings: string[];
}

/** One class of the orphan report. `hard` classes must read 0 after every import. */
export interface OrphanClass {
  hard: boolean;
  count: number;
  meaning: string;
  examples: Array<Record<string, unknown>>;
  /** Not run (it is expensive) — never read this as a clean 0. */
  skipped?: boolean;
}

export interface OrphanReport {
  hard_orphans: number;
  deep: boolean;
  classes: Record<string, OrphanClass>;
}

export interface OrphanReapResult {
  dry_run: boolean;
  would_remove?: Record<string, number>;
  removed?: Record<string, number>;
}

// ── Request / response envelopes ────────────────────────────────────────────

export interface DataSourceList {
  data_sources: DataSource[];
}

export interface DataSourceCreate {
  kind: "file" | "lab" | "gateway" | "manual";
  name: string;
  config?: Record<string, unknown>;
}

export interface ImportProfileList {
  profiles: ImportProfile[];
}

export interface ImportProfileCreate {
  data_source_id?: string;
  name: string;
  mapping: ImportMapping;
}

export interface ImportProfileUpdate {
  name?: string;
  mapping?: ImportMapping;
}

export interface ImportPreviewRequest {
  mapping?: ImportMapping;
  profile_id?: string;
  rows: Row[];
}

export interface ImportPreviewResult {
  row_count: number;
  records: CanonicalRecord[];
  truncated: boolean;
  /**
   * Resolved records per kind. The server seeds measurement/limit/production at
   * zero but tallies by kind, so `condition` is absent — not zero — when a mapping
   * resolved none. Read it as `?? 0`.
   */
  counts: {
    measurement: number;
    limit: number;
    production: number;
    condition?: number;
  };
  total: number;
  warnings: string[];
}

export interface ImportApplyRequest {
  mapping?: ImportMapping;
  profile_id?: string;
  data_source_id: string;
  filename: string;
  rows: Row[];
}

export interface ImportApplyResult {
  run: ImportRun;
  warnings: string[];
}

export interface ImportRunList {
  runs: ImportRun[];
}

export const twinImport = {
  dataSources: {
    list: () => api.get<DataSourceList>("/v1/twin/data-sources"),
    create: (body: DataSourceCreate) =>
      api.post<DataSource>("/v1/twin/data-sources", body),
    remove: (id: string) => api.delete<void>(`/v1/twin/data-sources/${id}`),
  },
  profiles: {
    list: () => api.get<ImportProfileList>("/v1/twin/import-profiles"),
    create: (body: ImportProfileCreate) =>
      api.post<ImportProfile>("/v1/twin/import-profiles", body),
    update: (id: string, body: ImportProfileUpdate) =>
      api.patch<ImportProfile>(`/v1/twin/import-profiles/${id}`, body),
    remove: (id: string) => api.delete<void>(`/v1/twin/import-profiles/${id}`),
  },
  runs: {
    list: () => api.get<ImportRunList>("/v1/twin/import-runs"),
    /**
     * Undo one run: removes exactly the rows it still owns. The server refuses (409)
     * whatever it cannot account for; `force` accepts deleting rows the run had taken
     * over from an earlier write (they cannot be restored).
     */
    undo: (id: string, force = false) =>
      api.delete<ImportUndoResult>(`/v1/twin/import-runs/${id}${force ? "?force=true" : ""}`),
  },
  orphans: {
    /** `deep` also scans imported readings for values no tag answers to (slower). */
    report: (deep = false) =>
      api.get<OrphanReport>(`/v1/twin/import/orphans${deep ? "?deep=1" : ""}`),
    /** A dry run unless `apply`; only ever removes provably-safe leftovers. */
    reap: (apply = false) =>
      api.post<OrphanReapResult>(`/v1/twin/import/orphans/reap${apply ? "?apply=true" : ""}`),
  },
  /** Dry-run — resolve rows against the mapping without persisting. */
  preview: (body: ImportPreviewRequest) =>
    api.post<ImportPreviewResult>("/v1/twin/import/preview", body),
  /** Persist — write the resolved readings / limits / batches; returns the run. */
  apply: (body: ImportApplyRequest) =>
    api.post<ImportApplyResult>("/v1/twin/import/apply", body),
};
